import { Button } from "@components/ui/button";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import { Control, FieldErrors, useFieldArray } from "react-hook-form";
import SchemaBlock from "./SchemaBlock";

type Props = {
  control: Control<any>;
  errors: FieldErrors<any>;
  name?: string;
};

const SchemasField = ({ control, errors, name = "json_ld" }: Props) => {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f.id === active.id);
    const newIndex = fields.findIndex((f) => f.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) move(oldIndex, newIndex);
  };

  const schemaErrors = (errors?.[name] as any) || undefined;

  return (
    <div className="flex flex-col gap-4 w-full min-w-0">
      {fields.length === 0 ? (
        <div className="text-sm text-gray-500 border border-dashed rounded-md p-6 text-center">
          No JSON-LD schemas added yet. Click "Add Schema" to insert one.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={fields.map((f) => f.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-3">
              {fields.map((field, index) => (
                <SchemaBlock
                  key={field.id}
                  id={field.id}
                  index={index}
                  control={control}
                  name={name}
                  error={schemaErrors?.[index]}
                  onRemove={() => remove(index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <div>
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            append({ name: "", content: "", order: fields.length })
          }
          className="gap-2"
        >
          <Plus size={16} />
          Add Schema
        </Button>
      </div>
    </div>
  );
};

export default SchemasField;
